import React from 'react'
import {all, call, delay, put, select, takeLatest} from 'redux-saga/effects'
import {get, isEmpty, isNil} from 'lodash'
import API_SERVICE from '../services/api'
import {extractState} from 'containers/pivotTable'
import types from '../actions/types'
import {generalError} from '../actions'
import {isGoogleDriveIntegrated} from 'selectors/auth'
import {Link} from 'react-router-dom'
import {getAllSelectedRows} from 'selectors/pivotTable'
import {snackbarMessage} from 'actions/snackbar'
import {setFolderDocumentsToRootDirectory} from 'actions/contacts'
import {generateCSVFile} from 'helpers/export'
import {getIds} from 'helpers/general/lists'
import {takeLatestWithKey} from 'helpers/saga'
import {t} from "helpers/language"
import {
  exportDataFinish,
  exportDataProgress,
  exportDataStart,
  loadData,
  loadDataFailure,
  loadDataSuccess,
  resetPage,
  saveDataFailure,
  saveDataSuccess,
} from 'actions/pivotTable'

const apiService = API_SERVICE.create()
const EXPORT_PAGE_SIZE = 500
const SELECTED_IDS_KEY = 'selectedIds'

export const createGenericPivotTableWatcher = (
  {
    loadData,
    exportData,
    useServerSideSearching = true,
  }
) => (
  api,
  stateKey,
  loadTypes,
  loadParams,
  loadFields,
  saveApi = api.update
) => {

  function* keyedSaga(params, func) {
    if (params.key === stateKey) yield func(params)
  }

  function* editData({entity, save}) {
    try {
      if (save) yield saveData([entity])
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* editDataMulti({entities, save}) {
    try {
      if (save) yield saveData(entities)
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* requestSave() {
    try {
      const edited = yield select(state => extractState(state, stateKey).edited)
      if (!isEmpty(edited)) {
        yield put(snackbarMessage('Saving your changes...', 'loading', false, false))
        const data = Object.keys(edited).map(k => edited[k])
        yield saveData(data)
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* saveData(data) {
    try {
      if (!isNil(saveApi)) {
        const response = yield call(saveApi, data)
        if (response !== false) {
          yield apiService.handleResponse(
            response,
            function* onOk(key, data) {
              yield put(saveDataSuccess(stateKey, data))
              yield put(snackbarMessage(t('Your changes have been saved'), 'checkmark'))
            },
            function* onFail(key, message, code) {
              yield put(saveDataFailure(stateKey, message, code))
              yield put(snackbarMessage(t('Your changes could not be saved: {1}', message), 'error', false))
            }
          )
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* reloadDataIfVisible(action) {
    let doLoad = action.loadPivotData  // reload if specifically asked
    if (!doLoad) {
      // automatically reload if visible
      doLoad = yield select(state => extractState(state, stateKey).visible)
    }
    if (doLoad) {
      yield put(resetPage(stateKey))
      yield loadData(api, stateKey, loadTypes, loadParams, loadFields)(action)
    }
  }

  function* searchFunctionality(p) {
    return !useServerSideSearching
      ? false
      : yield keyedSaga(p, loadData(api, stateKey, loadTypes, loadParams, loadFields))
  }

  function* watcher() {
    let watchArray = [
      // React to any pivot table event requiring a data reload...
      takeLatestWithKey(types.PIVOT_TABLE_SORT, p =>
        keyedSaga(p, loadData(api, stateKey, loadTypes, loadParams, loadFields)), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_SET_GROUPS, p =>
        keyedSaga(p, loadData(api, stateKey, loadTypes, loadParams, loadFields)), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_PAGE, p =>
        keyedSaga(p, loadData(api, stateKey, loadTypes, loadParams, loadFields)), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_SEARCH, searchFunctionality, 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_PAGE_SIZE_UPDATE, p =>
        keyedSaga(p, loadData(api, stateKey, loadTypes, loadParams, loadFields)), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_REQUEST_DATA, p =>
        keyedSaga(p, loadData(api, stateKey, loadTypes, loadParams, loadFields)), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_EDIT_DATA, p =>
        keyedSaga(p, editData), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_EDIT_DATA_MULTI, p =>
        keyedSaga(p, editDataMulti), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_SAVE_ATTEMPT, p =>
        keyedSaga(p, requestSave), 'key'),
      takeLatestWithKey(types.PIVOT_TABLE_EXPORT, p =>
        keyedSaga(p, exportData(api, stateKey, loadTypes, loadParams, loadFields)), 'key'),
    ]

    // ...as well as any other specified load actions
    if (loadTypes) watchArray = watchArray.concat(loadTypes.map(t => takeLatest(t, reloadDataIfVisible)))

    yield all(watchArray)
  }

  return watcher
}


function* getData(
  api,
  state,
  pivotState,
  loadTypes,
  loadParams,
  loadFields,
  addParams,
  start,
  limit,
  includeCount = true) {

  // sort by all groups ascending, then designated sort column
  const pivotSort = pivotState.sortColumn
  const pivotSortDirection = pivotState.sortDirection
  const pivotGroups = pivotState.groups

  let sortColumn = [pivotSort]
  let sortDirection = [pivotSortDirection]
  if (pivotGroups && pivotGroups.length > 0) {
    sortColumn = [...pivotGroups, ...sortColumn]
    sortDirection = [...pivotGroups.map(() => 'asc'), ...sortDirection]
  }

  let params = {
    sortColumn,
    sortDirection,
    count: includeCount,
    start,
    limit,
  }

  let search = pivotState.search
  if (search) {
    search = search.trim()
    if (search.length > 0)
      params['search'] = search
  }

  if (loadParams) {
    const loadedParams = typeof (loadParams) === 'function'
      ? loadParams(state)
      : loadParams

    if (loadedParams) {
      params = {
        ...params,
        ...loadedParams,
      }
    }
  }

  if (!isNil(addParams) && addParams.hasOwnProperty(SELECTED_IDS_KEY)) {
    params = {
      [SELECTED_IDS_KEY]: params[SELECTED_IDS_KEY],
      count: true,
    }
  }

  if (addParams)
    params = {...params, ...addParams}

  let fields = {default: true}
  if (typeof loadFields !== 'undefined') {
    fields = typeof (loadFields) === 'function'
      ? loadFields(state)
      : loadFields
  }

  const getRequest = get(api, 'get', null)
  if (isNil(getRequest))
    throw new Error('The API object does not have the get function', api)

  return yield call(api.get, params, fields, null, state)
}

// api - the direct api to call when loading the data for the pivot table
// stateKey - the unique key also used in the pivot table's reducer and component instance
//    (refers to the portion of the app state with dotted notation, e.g. 'context.selected.contacts')
// loadTypes - (optional) an array of additional actions to listen to and reload the pivot table data
//    (for example filter changes)
// loadParams - (optional) either a map of, or a function which returns a map of additional parameters for the load
//    (the full app state is supplied to the function)
// loadFields - (optional) either a map of, or a function which returns a map of fields and dependencies for the load
//    (the full app state is supplied to the function)
export const createPivotTableWatcher = createGenericPivotTableWatcher({
  loadData: (api, stateKey, loadTypes, loadParams, loadFields) => function* ({revertEdits, onlyReloadIfVisible}) {
    try {
      const state = yield select(state => state)
      const pivotState = get(state, stateKey, null)

      if (pivotState == null) {
        console.error('Pivot Table [saga]: No pivotTable element found in state under key [' + stateKey + ']')
        return
      }

      if (onlyReloadIfVisible === true && !pivotState.visible)
        return

      const start = pivotState.page * pivotState.pageSize || 0
      const limit = pivotState.pageSize || 30

      yield put(loadData(stateKey))

      const response = yield getData(
        api,
        state,
        pivotState,
        loadTypes,
        loadParams,
        loadFields,
        null,
        start,
        limit)

      yield apiService.handleResponse(
        response,
        function* onOk(key, data) {
          let result = data.result

          // Merge in edits
          if (revertEdits !== true) {
            const pivotState = yield select(state => get(state, stateKey))
            if (pivotState.edited) {

              result = result.map(entity => {
                const edited = pivotState.edited[entity.id]
                if (edited) {
                  entity = {...entity, _editedKeys: edited._editedKeys}
                  const _editedKeys = get(entity, '_editedKeys', null)
                  if (!isNil(_editedKeys)) {
                    Object.keys(edited._editedKeys).forEach(key => {
                      entity[key] = edited[key]
                    })
                  }
                }
                return entity
              })
            }
          }

          yield put(loadDataSuccess(stateKey, result, data.count, revertEdits, data))
        },
        function* onFail(key, message, code) {
          yield put(loadDataFailure(stateKey, message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  },
  exportData: (api, stateKey, loadTypes, loadParams, loadFields) => function* ({columns, fileName, params}) {
    try {
      const state = yield select(state => state)
      const pivotState = get(state, stateKey, null)

      if (pivotState == null) {
        console.error('Pivot Table [saga]: No pivotTable element found in state under key [' + stateKey + ']')
        return
      }

      let data = [], ok = true, error = null, pages = 1, rows = 1
      yield put(exportDataStart(stateKey))

      // Handle selected rows
      const selectedRows = yield select(state => getAllSelectedRows(state, stateKey))
      if (!isEmpty(selectedRows)) {
        params = isNil(params) ? {} : params
        params[SELECTED_IDS_KEY] = getIds(selectedRows)
      }

      for (let page = 0; page < pages; page++) {
        const response = yield getData(
          api,
          state,
          pivotState,
          loadTypes,
          loadParams,
          loadFields,
          params,
          page * EXPORT_PAGE_SIZE,
          EXPORT_PAGE_SIZE,
          (page === 0))

        if (!response.ok) {
          ok = false
          error = response.problem
          break
        }

        const result = response.data.data[0]
        if (!result.success) {
          ok = false
          error = result.message
          break
        }

        const resultData = result.result
        data = data.concat(resultData)

        if (page === 0) {
          rows = result.count
          pages = Math.ceil(rows / EXPORT_PAGE_SIZE)
        }

        yield put(exportDataProgress(stateKey, rows, data.length))
      }

      if (ok) {
        yield delay(500)

        const csvData = [
          columns.map(c => c.label || c.alias),
          ...data.map(d => {
            return columns.map(c => c.data ? c.data(d) : '')
          }),
        ]

        generateCSVFile(csvData, `${fileName}.csv`)
        yield put(exportDataFinish(stateKey, true))
      } else {
        yield put(snackbarMessage('Your data could not be exported: ' + error, 'error', false))
        yield put(exportDataFinish(stateKey, false, error))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  },
})

export const createGoogleCompanyDocumentWatcher = createGenericPivotTableWatcher({
  exportData: () => function* () { /* not supported */
  },
  loadData: (api, stateKey, loadTypes, loadParams) => function* () {
    try {
      const state = yield select(state => state)
      if (isGoogleDriveIntegrated(state)) {
        let params
        if (typeof loadParams === 'function') params = loadParams(state)

        const companyId = get(params, 'companyId')
        let folderId = get(params, 'folderId')

        if (!folderId && companyId) {
          const response = yield call(api.getCompanyDocumentsFolder, companyId)
          if (response.ok) {
            params.folderId = folderId = response.data.id
            yield put(setFolderDocumentsToRootDirectory(response.data))
          } else {
            yield put(loadDataFailure(stateKey, 'Could not load company documents', 500))
          }
        }

        if (isNil(folderId) && !isNil(companyId)) {
          const message = (
            <>
              {t('You need to select a folder in your Google Drive integration')} <Link
              to={'/my-account/integrations/filter/docs'}>
              {t('Google Drive Integration')}
            </Link>
            </>
          )
          yield put(snackbarMessage(message, 'error', false, true))
          return
        }

        if (!isNil(folderId)) {
          yield put(loadData(stateKey, params))
          const response = yield call(api.getDocuments, folderId)
          if (response.ok) {
            yield put(loadDataSuccess(stateKey, response.data.data, response.data.data.length))
          } else {
            yield put(loadDataFailure(stateKey, 'Could not load company documents', 500))
          }
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  },
})

export const createDocumentPivotTableWatcher = createGenericPivotTableWatcher({
  exportData: () => function* () { /* not supported */
  },
  loadData: (api, stateKey, loadTypes, loadParams) => function* () {
    try {
      const state = yield select(state => state)
      let params
      if (typeof loadParams === 'function') params = loadParams(state)

      yield put(loadData(stateKey, params))
      const response = yield call(api.getFolder, params)
      if (response.ok) {
        const documents = response.data.data[0].result
        yield put(loadDataSuccess(stateKey, documents, documents?.length))
      } else {
        yield put(loadDataFailure(stateKey, 'Could not load documents', 500))
      }

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  },
  useServerSideSearching: false,
})

export const createNonServerPivotTableWatcher = (stateKey) =>
  createGenericPivotTableWatcher({
    exportData: () => function* () { /* not supported */
    },
    loadData: () => function* () { /* not supported */
    },
  })(null, stateKey, [], null, null, null)
